import { GBIF_OCCURRENCE_HEX_PER_TILE } from '@/utils/map/gbifOccurrenceMap';

/** GBIF backbone genus Morchella — map heat density includes all Morchella spp. occurrences. */
export const GBIF_TAXON_MORCHELLA_GENUS = 2594601;


/**
 * In-memory session cache: normalized scientific name → GBIF backbone key used for tiles.
 * Avoids repeat calls to `species/match` when users switch away and back to the same species.
 */
const gbifSpeciesMatchCache = new Map<string, number>();

function normalizeScientificNameForCache(scientificName: string): string {
  return scientificName.trim().replace(/\s+/g, ' ');
}

/**
 * Resolve GBIF backbone taxon key for map tiles using the public species match API.
 * Sanity `taxonId` should match this key; when it does not, we still show correct tiles
 * and editors can align CMS values using the GBIF species URL.
 */
export async function resolveGbifSpeciesTaxonKey(
  scientificName: string,
  sanityTaxonId: number,
): Promise<number> {
  const name = normalizeScientificNameForCache(scientificName);
  if (!name || !Number.isFinite(sanityTaxonId) || sanityTaxonId <= 0) {
    return sanityTaxonId;
  }

  const cached = gbifSpeciesMatchCache.get(name);
  if (cached !== undefined) {
    if (process.env.NODE_ENV === 'development' && cached !== sanityTaxonId) {
      // eslint-disable-next-line no-console
      console.warn(
        `[GBIF] taxonId mismatch for "${name}": Sanity taxonId=${sanityTaxonId}, GBIF usageKey=${cached}. Update Studio taxonId to the GBIF backbone key (see species page URL on gbif.org).`,
      );
    }
    return cached;
  }

  const url = new URL('https://api.gbif.org/v1/species/match');
  url.searchParams.set('name', name);
  url.searchParams.set('verbose', 'false');

  try {
    const res = await fetch(url.toString());
    if (!res.ok) return sanityTaxonId;

    const data = (await res.json()) as {
      matchType?: string;
      usageKey?: number;
      acceptedUsageKey?: number;
    };

    let resolved: number;
    if (!data || data.matchType === 'NONE') {
      resolved = sanityTaxonId;
    } else {
      const key =
        typeof data.usageKey === 'number'
          ? data.usageKey
          : typeof data.acceptedUsageKey === 'number'
            ? data.acceptedUsageKey
            : sanityTaxonId;
      resolved = typeof key === 'number' ? key : sanityTaxonId;
    }

    gbifSpeciesMatchCache.set(name, resolved);

    if (process.env.NODE_ENV === 'development' && resolved !== sanityTaxonId) {
      // eslint-disable-next-line no-console
      console.warn(
        `[GBIF] taxonId mismatch for "${name}": Sanity taxonId=${sanityTaxonId}, GBIF usageKey=${resolved}. Update Studio taxonId to the GBIF backbone key (see species page URL on gbif.org).`,
      );
    }

    return resolved;
  } catch {
    return sanityTaxonId;
  }
}

/**
 * GBIF hex occurrence density as **Mapbox Vector Tiles** (`.mvt`). Client paints `fill-color` from
 * feature `total` so we can match product ramps; raster PNG tiles cannot be remapped.
 * @see https://techdocs.gbif.org/en/openapi/v2/maps
 */
export function buildGbifOccurrenceDensityMvtUrl(taxonKey: number): string {
  const params = new URLSearchParams({
    taxonKey: String(taxonKey),
    bin: 'hex',
    // Privacy-first: coarser bins keep overlays broad and less location-specific.
    hexPerTile: GBIF_OCCURRENCE_HEX_PER_TILE,
  });
  return `https://api.gbif.org/v2/map/occurrence/density/{z}/{x}/{y}.mvt?${params.toString()}`;
}
